/**
 * Workout Sync — daily check (one tap per day, plural wording).
 *
 * Today's workout sync block is always shown.
 * Past days where today's check is missing/incomplete are shown stacked above,
 * each with their own three-option row.
 *
 * Tapping "No, something's off" writes issue_found AND opens the Asana form.
 */

import { format, addDays } from "date-fns";
import type { Tester, Verification, VerificationStatus } from "../../types";
import { Section, CheckRow, StaleDayHeader } from "../Section";
import { ThreeOptionRow } from "../VerificationButtons";
import { WORKOUT_SYNC } from "../../lib/conditionKeys";
import { openAsanaBugForm } from "../../lib/asana";

interface WorkoutSyncSectionProps {
  profile: Tester;
  currentDayNum: number;
  windowDays: number;
  cohortStart: Date;
  asanaUrl: string;
  getStatus: (key: string, day: number) => Verification | null;
  setStatus: (input: {
    conditionKey: string;
    testingDay: number;
    status: VerificationStatus;
  }) => Promise<void>;
}

export function WorkoutSyncSection({
  profile,
  currentDayNum,
  cohortStart,
  asanaUrl,
  getStatus,
  setStatus,
}: WorkoutSyncSectionProps) {
  // Past days that need attention (today's day not included).
  const pastDays: number[] = [];
  for (let d = 1; d < currentDayNum; d++) {
    const row = getStatus(WORKOUT_SYNC, d);
    if (!row) pastDays.push(d);
  }

  const handleSelect = async (testingDay: number, status: VerificationStatus) => {
    await setStatus({ conditionKey: WORKOUT_SYNC, testingDay, status });
    if (status === "issue_found") {
      const dayDate = format(addDays(cohortStart, testingDay - 1), "EEE, MMM d");
      const dayLabel = `Day ${testingDay} of ${
        profile.tester_type === "Volunteer" ? "14" : "7"
      }`;
      openAsanaBugForm(asanaUrl, {
        email: profile.email,
        wordpressUserId: profile.wordpress_user_id,
        dayLabel,
        dayDate,
        conditionLabel: `Workout sync (Day ${testingDay} - ${dayDate})`,
      });
    }
  };

  const todayDate = format(addDays(cohortStart, currentDayNum - 1), "EEE, MMM d");
  const todayRow = getStatus(WORKOUT_SYNC, currentDayNum);

  return (
    <Section title="Workout syncs" meta={`Today · ${todayDate}`} accent="active">
      {pastDays.map((d) => {
        const date = format(addDays(cohortStart, d - 1), "EEE, MMM d");
        const row = getStatus(WORKOUT_SYNC, d);
        return (
          <div key={d}>
            <StaleDayHeader label={`Day ${d} · ${date} (overdue)`} />
            <div className="px-3 pb-2 pt-2.5 text-[13px] text-zinc-900">
              Do all fields across your workout syncs look correct?
            </div>
            <ThreeOptionRow
              status={row?.status ?? null}
              onSelect={(s) => handleSelect(d, s)}
              naLabel="N/A · no workouts"
            />
          </div>
        );
      })}

      {pastDays.length > 0 && (
        <StaleDayHeader label={`Today · ${todayDate}`} />
      )}

      <CheckRow
        label="Do all fields across your workout syncs look correct?"
        sub="Covers every workout you logged today."
        flagged={todayRow?.status === "issue_found"}
      />
      <ThreeOptionRow
        status={todayRow?.status ?? null}
        onSelect={(s) => handleSelect(currentDayNum, s)}
        naLabel="N/A · no workouts today"
      />

      {!asanaUrl && (
        <div className="px-3 py-2 text-[10px] text-zinc-400 italic border-t border-zinc-100">
          Bug form URL not yet configured. "No" taps will record the issue but won't open Asana.
        </div>
      )}
    </Section>
  );
}

